import { useState, useEffect, useCallback } from 'react';
import type { WeekType, WorkScheduleDayEntry, ScheduleEntryInput } from '@functieloonhuis/shared';
import { DAY_LABELS_FULL } from '../utils/weekUtils';

interface DayFormState {
  dayOfWeek: number;
  isWorkday: boolean;
  location: 'kantoor' | 'thuis' | null;
  isHalfDay: boolean;
  halfDayPeriod: 'ochtend' | 'middag' | null;
  startTime: string;
  endTime: string;
}

interface ScheduleFormProps {
  initialWeekA?: WorkScheduleDayEntry[];
  initialWeekB?: WorkScheduleDayEntry[];
  onSave: (entries: ScheduleEntryInput[]) => void;
  onCancel: () => void;
  onDelete?: () => void;
  isSaving: boolean;
}

function createDefaultWeek(): DayFormState[] {
  return [1, 2, 3, 4, 5, 6, 7].map(day => ({
    dayOfWeek: day,
    isWorkday: day <= 5,
    location: day <= 5 ? 'kantoor' as const : null,
    isHalfDay: false,
    halfDayPeriod: null,
    startTime: day <= 5 ? '08:30' : '',
    endTime: day <= 5 ? '17:00' : '',
  }));
}

function entriesToFormState(entries: WorkScheduleDayEntry[]): DayFormState[] {
  const base = createDefaultWeek().map(d => ({ ...d, isWorkday: false, location: null as 'kantoor' | 'thuis' | null, startTime: '', endTime: '' }));

  for (const entry of entries) {
    const idx = base.findIndex(d => d.dayOfWeek === entry.dayOfWeek);
    if (idx >= 0) {
      base[idx] = {
        dayOfWeek: entry.dayOfWeek,
        isWorkday: entry.isWorkday,
        location: entry.location ?? null,
        isHalfDay: entry.isHalfDay,
        halfDayPeriod: entry.halfDayPeriod ?? null,
        startTime: entry.startTime ?? '',
        endTime: entry.endTime ?? '',
      };
    }
  }

  return base;
}

export function ScheduleForm({ initialWeekA, initialWeekB, onSave, onCancel, onDelete, isSaving }: ScheduleFormProps) {
  const [weekA, setWeekA] = useState<DayFormState[]>(
    initialWeekA && initialWeekA.length > 0 ? entriesToFormState(initialWeekA) : createDefaultWeek()
  );
  const [weekB, setWeekB] = useState<DayFormState[]>(
    initialWeekB && initialWeekB.length > 0 ? entriesToFormState(initialWeekB) : createDefaultWeek().map(d => ({ ...d, isWorkday: false, location: null, startTime: '', endTime: '' }))
  );
  const [editingWeek, setEditingWeek] = useState<WeekType>('A');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Escape key to close delete modal
  const handleEscape = useCallback((e: KeyboardEvent) => {
    if (e.key === 'Escape' && showDeleteConfirm) {
      setShowDeleteConfirm(false);
    }
  }, [showDeleteConfirm]);

  useEffect(() => {
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleEscape]);

  const currentWeekData = editingWeek === 'A' ? weekA : weekB;
  const setCurrentWeekData = editingWeek === 'A' ? setWeekA : setWeekB;

  function updateDay(dayOfWeek: number, updates: Partial<DayFormState>) {
    setCurrentWeekData(prev =>
      prev.map(d => d.dayOfWeek === dayOfWeek ? { ...d, ...updates } : d)
    );
    // Clear errors for this field
    setErrors(prev => {
      const next = { ...prev };
      delete next[`${editingWeek}-${dayOfWeek}`];
      return next;
    });
  }

  function copyWeekAToB() {
    setWeekB(weekA.map(d => ({ ...d })));
  }

  function validate(): boolean {
    const newErrors: Record<string, string> = {};

    // Validate Week A has at least 1 workday
    if (!weekA.some(d => d.isWorkday)) {
      newErrors['weekA-general'] = 'Week A moet minimaal 1 werkdag bevatten';
    }

    const validateWeek = (days: DayFormState[], weekType: string) => {
      for (const day of days) {
        if (day.isWorkday) {
          const dayErrors: string[] = [];
          if (!day.location) {
            dayErrors.push('Locatie is verplicht voor werkdagen');
          }
          if (day.isHalfDay && !day.halfDayPeriod) {
            dayErrors.push('Kies ochtend of middag');
          }
          if (day.startTime && day.endTime && day.startTime >= day.endTime) {
            dayErrors.push('Eindtijd moet na starttijd liggen');
          }
          if (dayErrors.length > 0) {
            newErrors[`${weekType}-${day.dayOfWeek}`] = dayErrors.join('. ');
          }
        }
      }
    };

    validateWeek(weekA, 'A');
    // Only validate week B if it has workdays
    if (weekB.some(d => d.isWorkday)) {
      validateWeek(weekB, 'B');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  function handleSubmit() {
    if (!validate()) return;

    const entries: ScheduleEntryInput[] = [];

    const processWeek = (days: DayFormState[], weekType: WeekType) => {
      for (const day of days) {
        entries.push({
          weekType,
          dayOfWeek: day.dayOfWeek,
          isWorkday: day.isWorkday,
          location: day.isWorkday ? day.location : null,
          isHalfDay: day.isWorkday ? day.isHalfDay : false,
          halfDayPeriod: day.isHalfDay ? day.halfDayPeriod : null,
          startTime: day.isWorkday && day.startTime ? day.startTime : null,
          endTime: day.isWorkday && day.endTime ? day.endTime : null,
        });
      }
    };

    processWeek(weekA, 'A');
    if (weekB.some(d => d.isWorkday)) {
      processWeek(weekB, 'B');
    }

    onSave(entries);
  }

  return (
    <div className="space-y-4">
      {/* Week toggle for form editing */}
      <div className="flex items-center justify-between">
        <div className="inline-flex rounded-lg border border-gray-200 p-1 bg-gray-50">
          <button
            type="button"
            onClick={() => setEditingWeek('A')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              editingWeek === 'A' ? 'bg-indigo-600 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Week A
          </button>
          <button
            type="button"
            onClick={() => setEditingWeek('B')}
            className={`px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${
              editingWeek === 'B' ? 'bg-purple-600 text-white shadow-sm' : 'text-gray-600 hover:text-gray-900'
            }`}
          >
            Week B
          </button>
        </div>

        {editingWeek === 'B' && (
          <button
            type="button"
            onClick={copyWeekAToB}
            className="text-sm text-indigo-600 hover:text-indigo-800 font-medium"
          >
            Kopieer Week A
          </button>
        )}
      </div>

      {errors['weekA-general'] && (
        <p className="text-sm text-red-600">{errors['weekA-general']}</p>
      )}

      {/* Day rows */}
      <div className="space-y-3">
        {currentWeekData.map(day => (
          <DayRow
            key={day.dayOfWeek}
            day={day}
            error={errors[`${editingWeek}-${day.dayOfWeek}`]}
            onChange={(updates) => updateDay(day.dayOfWeek, updates)}
          />
        ))}
      </div>

      {/* Action buttons */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <div>
          {onDelete && (
            <button
              type="button"
              onClick={() => setShowDeleteConfirm(true)}
              className="px-4 py-2 text-sm font-medium text-red-600 hover:text-red-800"
            >
              Verwijderen
            </button>
          )}
        </div>
        <div className="flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={isSaving}
            className="px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
          >
            Annuleren
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={isSaving}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
          >
            {isSaving && (
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            )}
            Opslaan
          </button>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
              <div className="sm:flex sm:items-start">
                <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-red-100 sm:mx-0 sm:h-10 sm:w-10">
                  <svg className="h-6 w-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                </div>
                <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Werkrooster verwijderen
                  </h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Weet je zeker dat je dit werkrooster wilt verwijderen? Dit kan niet ongedaan worden gemaakt.
                  </p>
                </div>
              </div>
              <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => { setShowDeleteConfirm(false); onDelete?.(); }}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-red-600 text-base font-medium text-white hover:bg-red-700 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Verwijderen
                </button>
                <button
                  type="button"
                  onClick={() => setShowDeleteConfirm(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 sm:mt-0 sm:w-auto sm:text-sm"
                >
                  Annuleren
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

interface DayRowProps {
  day: DayFormState;
  error?: string;
  onChange: (updates: Partial<DayFormState>) => void;
}

function DayRow({ day, error, onChange }: DayRowProps) {
  const dayLabel = DAY_LABELS_FULL[day.dayOfWeek] ?? `Dag ${day.dayOfWeek}`;

  return (
    <div className={`p-3 rounded-lg border ${error ? 'border-red-300 bg-red-50' : 'border-gray-200 bg-white'}`}>
      <div className="flex flex-wrap items-center gap-3">
        {/* Day label */}
        <span className="w-24 text-sm font-medium text-gray-700">{dayLabel}</span>

        {/* Workday toggle */}
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={day.isWorkday}
            onChange={(e) => onChange({
              isWorkday: e.target.checked,
              location: e.target.checked ? (day.location ?? 'kantoor') : null,
              startTime: e.target.checked ? (day.startTime || '08:30') : '',
              endTime: e.target.checked ? (day.endTime || '17:00') : '',
            })}
            className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
          />
          <span className="text-sm text-gray-600">Werkdag</span>
        </label>

        {/* Conditional fields for workdays */}
        {day.isWorkday && (
          <>
            {/* Location */}
            <select
              value={day.location ?? ''}
              onChange={(e) => onChange({ location: e.target.value as 'kantoor' | 'thuis' })}
              className="rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
            >
              <option value="kantoor">Kantoor</option>
              <option value="thuis">Thuis</option>
            </select>

            {/* Times */}
            <div className="flex items-center gap-1">
              <input
                type="time"
                value={day.startTime}
                onChange={(e) => onChange({ startTime: e.target.value })}
                className="rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 w-[110px]"
              />
              <span className="text-gray-400">-</span>
              <input
                type="time"
                value={day.endTime}
                onChange={(e) => onChange({ endTime: e.target.value })}
                className="rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500 w-[110px]"
              />
            </div>

            {/* Half day */}
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={day.isHalfDay}
                onChange={(e) => onChange({
                  isHalfDay: e.target.checked,
                  halfDayPeriod: e.target.checked ? 'ochtend' : null,
                })}
                className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm text-gray-600">Halve dag</span>
            </label>

            {day.isHalfDay && (
              <select
                value={day.halfDayPeriod ?? ''}
                onChange={(e) => onChange({ halfDayPeriod: e.target.value as 'ochtend' | 'middag' })}
                className="rounded-md border-gray-300 text-sm shadow-sm focus:border-indigo-500 focus:ring-indigo-500"
              >
                <option value="ochtend">Ochtend</option>
                <option value="middag">Middag</option>
              </select>
            )}
          </>
        )}
      </div>

      {error && (
        <p className="mt-1 text-xs text-red-600">{error}</p>
      )}
    </div>
  );
}
